import React, { useState, useEffect } from 'react';
import { useUser } from '../context/UserContext';
import { db, functions } from '../firebaseConfig';
import { toast, ToastContainer } from 'react-toastify';
import {
  collection,
  getDocs,
  query,
  orderBy,
  startAt,
  endAt,
  limit,
  addDoc,
} from 'firebase/firestore';
import './BoloPage.css';
import { httpsCallable } from 'firebase/functions';
import { getStorage, ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import ActiveBolos from './ActiveBolos';
import PageInfoButton from '../components/PageInfoButton';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faEarthAmericas,
  faClock,
  faLocationDot,
  faPaperPlane,
  faPaperclip,
  faShieldHalved,
  faUsers,
  faXmark,
} from '@fortawesome/free-solid-svg-icons';

const RECIPIENT_FIELDS = {
  country: {
    label: 'Country',
    optionType: 'countries',
    placeholder: 'Type to filter countries...',
  },
  stateProvince: {
    label: 'State / Province',
    optionType: 'stateProvinces',
    placeholder: 'Type to filter states or provinces...',
  },
  city: {
    label: 'City',
    optionType: 'cities',
    placeholder: 'Type to filter cities...',
  },
};

const getOptionLabel = (option) => option?.label || option?.display || '';

const RECIPIENT_ICONS = {
  country: faEarthAmericas,
  city: faLocationDot,
};

const StateProvinceIcon = () => (
  <svg
    className="recipient-type-icon recipient-map-outline-icon"
    viewBox="0 0 24 24"
    aria-hidden="true"
    focusable="false"
  >
    <path d="M3.5 5.5 8.8 3l6.4 2.5 5.3-2.3v15.3l-5.3 2.3-6.4-2.5-5.3 2.2V5.5Z" />
    <path d="M8.8 3v15.3" />
    <path d="M15.2 5.5v15.3" />
  </svg>
);

const RecipientTypeIcon = ({ type }) => {
  if (type === 'stateProvince') return <StateProvinceIcon />;
  return (
    <FontAwesomeIcon
      icon={RECIPIENT_ICONS[type]}
      className="recipient-type-icon"
    />
  );
};

const normalizeLower = (value) => (value || '').trim().toLowerCase();

const makeRecipientKey = (target) => {
  if (!target) return '';
  if (target.type === 'country') return `country:${target.countryLower}`;
  if (target.type === 'stateProvince') {
    return `state:${target.countryLower}|${target.stateProvinceLower}`;
  }
  if (target.type === 'city') {
    return `city:${target.countryLower}|${target.stateProvinceLower}|${target.cityLower}`;
  }
  return '';
};

const getShortRecipientLabel = (target) => {
  if (!target) return '';
  return (
    target.city ||
    target.stateProvince ||
    target.country ||
    (target.label || '').split(',')[0].trim()
  );
};

const isRecipientAncestorOf = (ancestor, descendant) => {
  if (!ancestor || !descendant) return false;

  if (ancestor.type === 'country') {
    return (
      descendant.type !== 'country' &&
      ancestor.countryLower &&
      ancestor.countryLower === descendant.countryLower
    );
  }

  if (ancestor.type === 'stateProvince') {
    return (
      descendant.type === 'city' &&
      ancestor.countryLower &&
      ancestor.stateProvinceLower &&
      ancestor.countryLower === descendant.countryLower &&
      ancestor.stateProvinceLower === descendant.stateProvinceLower
    );
  }

  return false;
};

const getRecipientDisableReason = (optionTarget, selectedTargets) => {
  const optionKey = makeRecipientKey(optionTarget);

  for (const selectedTarget of selectedTargets) {
    const selectedKey = makeRecipientKey(selectedTarget);
    if (!selectedKey || selectedKey === optionKey) continue;

    if (isRecipientAncestorOf(selectedTarget, optionTarget)) {
      return `Included in ${getShortRecipientLabel(selectedTarget)}`;
    }

    if (isRecipientAncestorOf(optionTarget, selectedTarget)) {
      return `${getShortRecipientLabel(selectedTarget)} is selected`;
    }
  }

  return '';
};

const getUserAudienceKeys = (user) => {
  const keys = [];
  const countryLower = normalizeLower(user?.countryLower || user?.country);
  const stateProvinceLower = normalizeLower(
    user?.stateProvinceLower || user?.stateProvince || user?.state
  );
  const cityLower = normalizeLower(user?.cityLower || user?.city);

  if (user?.uid) keys.push(`user:${user.uid}`);
  if (countryLower) keys.push(`country:${countryLower}`);
  if (countryLower && stateProvinceLower) {
    keys.push(`state:${countryLower}|${stateProvinceLower}`);
  }
  if (countryLower && stateProvinceLower && cityLower) {
    keys.push(`city:${countryLower}|${stateProvinceLower}|${cityLower}`);
  }

  return keys;
};

const BoloPage = () => {
  const storage = getStorage(undefined, 'coldwire-f7262.firebasestorage.app');
  const { currentUser } = useUser();

  const [activeTab, setActiveTab] = useState('active');
  const [title, setTitle] = useState('');
  const [message, setMessage] = useState('');
  const [expiration, setExpiration] = useState('');
  const [attachedFiles, setAttachedFiles] = useState([]);
  const [selectedRecipients, setSelectedRecipients] = useState([]);
  const [isRecipientModalOpen, setIsRecipientModalOpen] = useState(false);
  const [recipientFields, setRecipientFields] = useState({});
  const [recipientOptions, setRecipientOptions] = useState({});
  const [recipientFilters, setRecipientFilters] = useState({});
  const [errors, setErrors] = useState([]);
  const [isSending, setIsSending] = useState(false);

  useEffect(() => {
    if (!isRecipientModalOpen) {
      setRecipientFields({});
      setRecipientFilters({});
      setRecipientOptions({});
    }
  }, [isRecipientModalOpen]);

  const loadRecipientOptions = async (field, filterValue = '') => {
    const config = RECIPIENT_FIELDS[field];
    if (!config) return;

    try {
      const normalizedFilter = filterValue.trim().toLowerCase();
      const optionsRef = collection(db, 'directoryOptions', config.optionType, 'values');
      const constraints = [orderBy('lower')];

      if (normalizedFilter) {
        constraints.push(startAt(normalizedFilter));
        constraints.push(endAt(`${normalizedFilter}\uf8ff`));
      }

      constraints.push(limit(25));

      const snap = await getDocs(query(optionsRef, ...constraints));
      const options = snap.docs
        .map((docSnap) => ({ id: docSnap.id, ...docSnap.data() }))
        .sort((a, b) => getOptionLabel(a).localeCompare(getOptionLabel(b)));

      setRecipientOptions((prev) => ({ ...prev, [field]: options }));
    } catch (err) {
      console.error('Failed to load recipient options:', err);
      setRecipientOptions((prev) => ({ ...prev, [field]: [] }));
    }
  };

  const handleAddRecipientField = (field) => {
    if (!field || Object.prototype.hasOwnProperty.call(recipientFields, field)) return;
    setRecipientFields((prev) => ({ ...prev, [field]: [] }));
    setRecipientFilters((prev) => ({ ...prev, [field]: '' }));
    void loadRecipientOptions(field, '');
  };

  const handleRemoveRecipientField = (field) => {
    setRecipientFields((prev) => {
      const next = { ...prev };
      delete next[field];
      return next;
    });
    setRecipientFilters((prev) => {
      const next = { ...prev };
      delete next[field];
      return next;
    });
  };

  const buildRecipientTarget = (field, option) => {
    if (!option?.lower) return null;

    if (field === 'country') {
      return {
        type: 'country',
        label: getOptionLabel(option),
        countryLower: option.lower,
      };
    }

    if (field === 'stateProvince') {
      return {
        type: 'stateProvince',
        label: getOptionLabel(option),
        countryLower: option.countryLower,
        stateProvinceLower: option.lower,
      };
    }

    if (field === 'city') {
      return {
        type: 'city',
        label: getOptionLabel(option),
        countryLower: option.countryLower,
        stateProvinceLower: option.stateProvinceLower,
        cityLower: option.lower,
      };
    }

    return null;
  };

  const getModalSelectedRecipientTargets = () => (
    Object.entries(recipientFields)
      .flatMap(([field, options]) => (
        (Array.isArray(options) ? options : [])
          .map((option) => buildRecipientTarget(field, option))
      ))
      .filter(Boolean)
      .filter((target) => makeRecipientKey(target))
  );

  const getActiveRecipientTargets = () => [
    ...selectedRecipients,
    ...getModalSelectedRecipientTargets(),
  ];

  const handleAddSelectedRecipients = () => {
    const targets = Object.entries(recipientFields)
      .flatMap(([field, options]) => (
        (Array.isArray(options) ? options : [])
          .map((option) => buildRecipientTarget(field, option))
      ))
      .filter(Boolean)
      .filter((target) => makeRecipientKey(target));

    if (targets.length === 0) {
      toast.error('Select at least one city, state/province, or country.');
      return;
    }

    setSelectedRecipients((prev) => {
      const existingKeys = new Set(prev.map((recipient) => recipient.key));
      const acceptedTargets = [...prev];
      const nextRecipients = targets
        .map((target) => ({ ...target, key: makeRecipientKey(target) }))
        .filter((target) => {
          if (existingKeys.has(target.key)) return false;
          const disableReason = getRecipientDisableReason(target, acceptedTargets);
          if (disableReason) return false;
          acceptedTargets.push(target);
          existingKeys.add(target.key);
          return true;
        });
      return [...prev, ...nextRecipients];
    });
    setErrors([]);
    setIsRecipientModalOpen(false);
  };

  const validateIntel = () => {
    const nextErrors = [];
    if (selectedRecipients.length === 0) {
      nextErrors.push('At least one recipient must be selected.');
    }
    if (!title.trim()) nextErrors.push('Title is required.');
    if (!message.trim()) nextErrors.push('Message text is required.');
    if (!expiration) nextErrors.push('Expiration time must be set.');
    return nextErrors;
  };

  const handleSendIntel = async () => {
    if (!currentUser) return;

    const recipientKeys = selectedRecipients.map((recipient) => recipient.key);
    const authorKey = `user:${currentUser.uid}`;
    const audienceKeys = [...new Set([...recipientKeys, authorKey])];
    const authorAlreadyInAudience = getUserAudienceKeys(currentUser).some((key) =>
      recipientKeys.includes(key)
    );

    let fileUrls = [];

    if (attachedFiles.length > 0) {
      try {
        for (const item of attachedFiles) {
          const fileRef = ref(storage, `intel/${Date.now()}_${item.file.name}`);
          await uploadBytes(fileRef, item.file);
          const url = await getDownloadURL(fileRef);
          fileUrls.push(url);
        }
      } catch (err) {
        console.error('Failed to upload one or more files:', err);
        toast.error('Failed to upload attachments.');
        return;
      }
    }

    const createdAt = new Date();
    const expirationHours = parseInt(expiration, 10);
    const expiresAt = new Date(createdAt.getTime() + expirationHours * 60 * 60 * 1000);
    const senderName = `${currentUser.rank || ''} ${currentUser.firstInitialLastName || ''}, ${currentUser.agency || ''}, ${currentUser.stateProvince || currentUser.state || ''}`;

    const intelData = {
      title: title.trim(),
      text: message.trim(),
      recipients: selectedRecipients,
      recipientKeys,
      audienceKeys,
      authorIncludedByLocation: authorAlreadyInAudience,
      expiration,
      senderId: currentUser.uid,
      senderName,
      createdAt,
      expiresAt,
      attachedFileUrls: fileUrls,
      type: 'intel',
    };

    try {
      const intelRef = await addDoc(collection(db, 'intel'), intelData);

      const sendIntelNotification = httpsCallable(functions, 'sendIntelNotification');
      const notificationResult = await sendIntelNotification({
        intelId: intelRef.id,
        title: intelData.title,
        text: intelData.text,
        recipients: intelData.recipients,
        recipientKeys,
        authorUid: currentUser.uid,
        sender: senderName,
      });

      console.info('Intel notification result:', notificationResult.data);

      if (notificationResult.data?.success) {
        toast.success(
          `Intel sent. Notifications: ${notificationResult.data.sent}/${notificationResult.data.total}`
        );
      } else {
        toast.warn('Intel sent, but no push notifications delivered.');
      }

      setTitle('');
      setMessage('');
      setSelectedRecipients([]);
      setExpiration('');
      setAttachedFiles([]);
      setErrors([]);
      const uploadInput = document.getElementById('intel-picture-upload');
      if (uploadInput) uploadInput.value = '';
    } catch (error) {
      console.error('Error saving or sending Intel:', error);
      toast.error('Failed to send Intel. Please try again.');
    }
  };

  return (
    <div className="bolo-page">
      <div className="page-info-header-block">
        <h2 style={{ margin: 0, fontSize: '28px', textAlign: 'center' }}>Intel Hub</h2>
        <div className="page-info-button-wrapper">
          <PageInfoButton title="About the Intel Hub">
            <p>Share time-sensitive intelligence with ColdWire members by location.</p>
            <ul className="page-info-list">
              <li><strong>Active tab:</strong> Review active Intel messages and contact the sender directly by clicking the name at the bottom of the message.</li>
              <li><strong>Send tab:</strong> Create Intel messages for recipients by city, state/province, or country.</li>
              <li><strong>Author copy:</strong> The sender receives the Intel too, even if their profile location is outside the selected recipients.</li>
              <li><strong>Attachments & lifecycle:</strong> You can add relevant files and set expirations. Intel messages auto-expire after the selected time.</li>
            </ul>
          </PageInfoButton>
        </div>
      </div>

      <div className="bolo-tabs">
        <button className={activeTab === 'active' ? 'active' : ''} onClick={() => setActiveTab('active')}>Active</button>
        <button className={activeTab === 'send' ? 'active' : ''} onClick={() => setActiveTab('send')}>Send</button>
      </div>

      {activeTab === 'active' && <ActiveBolos />}

      {activeTab === 'send' && (
        <div className="send-bolo-form intel">
          <h2 className="page-title" style={{ color: '#007bff' }}>
            <FontAwesomeIcon icon={faPaperPlane} className="page-title-icon" />
            <span>Send Intel</span>
          </h2>

          <div className="bolo-section">
            <div className="recipient-header-row">
              <h4>
                Recipients <span style={{ color: 'red' }}>*</span>
              </h4>
              <button
                type="button"
                onClick={() => setIsRecipientModalOpen(true)}
                className="dropdown-toggle intel"
              >
                <FontAwesomeIcon icon={faUsers} className="button-icon" />
                Select Recipients
              </button>
            </div>

            {selectedRecipients.length === 0 ? (
              <p style={{ color: '#888' }}>No recipients selected yet.</p>
            ) : (
              <ul className="recipient-list">
                {selectedRecipients.map((recipient) => (
                  <li key={recipient.key} className="recipient-row">
                    <div className="recipient-info">
                      <span className="recipient-name">
                        <RecipientTypeIcon type={recipient.type} />
                        <strong>{RECIPIENT_FIELDS[recipient.type]?.label}:</strong> {recipient.label}
                      </span>
                    </div>
                    <button
                      type="button"
                      className="remove-recipient-button"
                      onClick={() => {
                        setSelectedRecipients((prev) =>
                          prev.filter((item) => item.key !== recipient.key)
                        );
                        setErrors([]);
                      }}
                      aria-label={`Remove ${recipient.label}`}
                    >
                      <FontAwesomeIcon icon={faXmark} />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className="bolo-section">
            <label>
              Title: <span style={{ color: 'red' }}>*</span>
            </label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g. Case lead, vehicle sighting, subject information..."
              style={{ width: '100%', marginBottom: '8px' }}
            />
          </div>

          <div className="bolo-section">
            <label>
              Intel Message: <span style={{ color: 'red' }}>*</span>
            </label>
            <textarea
              value={message}
              onChange={(e) => {
                setMessage(e.target.value);
                e.target.style.height = 'auto';
                e.target.style.height = Math.max(e.target.scrollHeight, 180) + 'px';
              }}
              placeholder="Enter Intel message here..."
              style={{ minHeight: '180px', width: '100%', resize: 'none' }}
            />
          </div>

          <div className="bolo-section">
            <input
              type="file"
              accept="image/*"
              id="intel-picture-upload"
              multiple
              style={{ display: 'none' }}
              onChange={(e) => {
                const files = Array.from(e.target.files).map((file) => ({
                  file,
                  previewUrl: URL.createObjectURL(file),
                }));
                setAttachedFiles((prev) => [...prev, ...files]);
              }}
            />
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                document.getElementById('intel-picture-upload').click();
              }}
              className="upload-picture-button intel-action-button"
            >
              <FontAwesomeIcon icon={faPaperclip} className="button-icon" />
              Attach Images
            </button>

            {attachedFiles.length > 0 && (
              <div className="attachment-preview">
                {attachedFiles.map((item, idx) => (
                  <div key={item.previewUrl} className="attachment-item">
                    <img src={item.previewUrl} alt="Preview" />
                    <button
                      type="button"
                      onClick={(e) => {
                        e.preventDefault();
                        URL.revokeObjectURL(item.previewUrl);
                        setAttachedFiles((prev) => prev.filter((_, i) => i !== idx));
                      }}
                    >
                      X
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="bolo-section send-row">
            <div>
              <label>
                <FontAwesomeIcon icon={faClock} className="field-label-icon" />
                Expires In: <span style={{ color: 'red' }}>*</span>
              </label>
              <select value={expiration} onChange={(e) => setExpiration(e.target.value)}>
                <option value="">Select duration</option>
                <option value="12">12 hours</option>
                <option value="24">24 hours</option>
                <option value="48">48 hours</option>
              </select>
            </div>

            <button
              type="button"
              onClick={() => {
                if (isSending) return;
                const nextErrors = validateIntel();
                if (nextErrors.length > 0) {
                  setErrors(nextErrors);
                  return;
                }
                setIsSending(true);
                handleSendIntel().finally(() => setIsSending(false));
              }}
              className="send-bolo-button intel"
              disabled={isSending}
            >
              <FontAwesomeIcon icon={faShieldHalved} className="button-icon" />
              {isSending ? 'Sending...' : 'Send Intel'}
            </button>
          </div>

          {errors.length > 0 && (
            <div style={{ marginTop: '8px' }}>
              {errors.map((err) => (
                <p key={err} style={{ color: 'red', margin: 0 }}>
                  {err}
                </p>
              ))}
            </div>
          )}
        </div>
      )}

      {isRecipientModalOpen && (
        <div className="modal-overlay" onClick={() => setIsRecipientModalOpen(false)}>
          <div className="intel-recipient-modal" onClick={(e) => e.stopPropagation()}>
            <div className="search-field-row">
              <label>
                Select by:
                <select
                  onChange={(e) => handleAddRecipientField(e.target.value)}
                  value=""
                  style={{ marginLeft: '8px' }}
                >
                  <option value="">Select recipient type</option>
                  {Object.entries(RECIPIENT_FIELDS)
                    .filter(([field]) => !Object.prototype.hasOwnProperty.call(recipientFields, field))
                    .map(([field, config]) => (
                      <option key={field} value={field}>{config.label}</option>
                    ))}
                </select>
              </label>
            </div>

            {Object.entries(recipientFields).map(([field, value]) => {
              const config = RECIPIENT_FIELDS[field];
              const options = recipientOptions[field] || [];
              const filter = recipientFilters[field] || '';
              const selectedOptions = Array.isArray(value) ? value : [];
              const selectedOptionIds = new Set(selectedOptions.map((option) => option.id));
              return (
                <div key={field} className="search-field-row">
                  <div className="search-field-heading">
                    <div className="search-field-label">{config.label}</div>
                    <button
                      type="button"
                      onClick={() => handleRemoveRecipientField(field)}
                      className="search-field-remove"
                      title="Remove filter"
                      aria-label={`Remove ${config.label} filter`}
                    >
                      X
                    </button>
                  </div>

                  <div className="agency-select-panel">
                    <input
                      type="text"
                      value={filter}
                      onChange={(e) => {
                        const nextFilter = e.target.value;
                        setRecipientFilters((prev) => ({ ...prev, [field]: nextFilter }));
                        void loadRecipientOptions(field, nextFilter);
                      }}
                      placeholder={config.placeholder}
                      className="agency-select-filter"
                    />
                    <div className="agency-select-options">
                      {options.map((option) => {
                        const optionTarget = buildRecipientTarget(field, option);
                        const checked = selectedOptionIds.has(option.id);
                        const disableReason = getRecipientDisableReason(
                          optionTarget,
                          getActiveRecipientTargets()
                        );
                        const isDisabled = Boolean(disableReason);
                        const toggleOption = () => {
                          if (isDisabled) return;
                          setRecipientFields((prev) => {
                            const current = Array.isArray(prev[field]) ? prev[field] : [];
                            const exists = current.some((item) => item.id === option.id);
                            return {
                              ...prev,
                              [field]: exists
                                ? current.filter((item) => item.id !== option.id)
                                : [...current, option],
                            };
                          });
                        };
                        return (
                          <div
                            key={option.id}
                            role="button"
                            tabIndex={isDisabled ? -1 : 0}
                            aria-disabled={isDisabled}
                            className={
                              'agency-select-option' +
                              (checked ? ' selected' : '') +
                              (isDisabled ? ' disabled' : '')
                            }
                            onClick={toggleOption}
                            onKeyDown={(e) => {
                              if (isDisabled) return;
                              if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                toggleOption();
                              }
                            }}
                          >
                            <input
                              type="checkbox"
                              checked={checked}
                              disabled={isDisabled}
                              readOnly
                              tabIndex={-1}
                            />
                            <span className="agency-select-option-text">
                              <span>{getOptionLabel(option)}</span>
                              {disableReason && (
                                <span className="agency-select-disable-reason">
                                  {disableReason}
                                </span>
                              )}
                            </span>
                          </div>
                        );
                      })}
                      {options.length === 0 && (
                        <div className="agency-select-empty">No options found</div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}

            <div className="search-modal-buttons">
              <button type="button" onClick={handleAddSelectedRecipients}>Add Recipients</button>
              <button type="button" onClick={() => setIsRecipientModalOpen(false)}>Close</button>
            </div>
          </div>
        </div>
      )}

      <ToastContainer />
    </div>
  );
};

export default BoloPage;
